import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import { ChevronLeftOutlined, EditOutlined } from "@mui-symbols-material/w300"
import { Link, Outlet, useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"

import { AdjustmentDialog } from "../../../Adjustment/components"
import { ALL_WAREHOUSES_VALUE } from "../../constants"
import { ProductInventoryProvider } from "../../contexts"
import { useGetProductInventory } from "../../hooks"

import { ProductInventoryHeader } from "./ProductInventoryHeader"
import { ProductInventorySearchSuggestion } from "./ProductInventorySearchSuggestion"
import { ProductInventoryStats } from "./ProductInventoryStats"

import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

export const ProductInventoryDetailsPage: FC = () => {
  const params = useParams({ from: "/app/inventory/products/$productId" })
  const { warehouseAreaId } = useSearch({ from: "/app/inventory/products/$productId/_layout" })
  const location = useLocation()
  const navigate = useNavigate()

  const productId = Number(params.productId)

  const [isAdjustmentDialogOpen, setIsAdjustmentDialogOpen] = useState(false)

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.INVENTORY_MAINTENANCE])

  const { inventory } = useGetProductInventory(productId)

  const tabs = useMemo(
    () => [
      { path: `/app/inventory/products/${productId}/transactions`, label: "Transactions" },
      { path: `/app/inventory/products/${productId}/lots`, label: "By Lot" },
      { path: `/app/inventory/products/${productId}/pallets`, label: "By Pallet" },
    ],
    [productId]
  )

  const activeTab = tabs.find((tab) => location.pathname === tab.path) ?? tabs[0]

  const handleWarehouseChange = useCallback(
    (warehouseAreaId: string) => {
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          warehouseAreaId: warehouseAreaId === ALL_WAREHOUSES_VALUE ? undefined : warehouseAreaId,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  if (!inventory) return null

  return (
    <Paper
      elevation={0}
      className='flex min-h-0 grow flex-col gap-6 overflow-hidden rounded-lg bg-white p-1'
      data-testid='product-inventory.details-page'
    >
      <Box className='flex items-center justify-between gap-4'>
        <Box className='mr-auto flex items-center gap-4'>
          <Link to='..'>
            <IconButton aria-label='back to list' className='rounded bg-gray-200 text-primary'>
              <ChevronLeftOutlined />
            </IconButton>
          </Link>
          <Typography variant='h5' color='primary' fontWeight='normal' component='h1'>
            Product Inventory Details
          </Typography>
        </Box>
        <ProductInventorySearchSuggestion />
        {canMaintain && (
          <Button
            appearance='outlined'
            startIcon={<EditOutlined />}
            onClick={() => setIsAdjustmentDialogOpen(true)}
            data-testid='product-inventory.details-page.adjust-button'
          >
            Adjust
          </Button>
        )}
      </Box>
      <ProductInventoryHeader product={inventory.product} />
      <ProductInventoryStats
        inventory={inventory}
        warehouseAreaId={warehouseAreaId}
        onWarehouseChange={handleWarehouseChange}
      />
      <Box className='flex w-full flex-1 flex-col overflow-hidden'>
        <Tabs value={activeTab.path} variant='scrollable' scrollButtons='auto'>
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              label={tab.label}
              value={tab.path}
              component={Link as any}
              to={tab.path}
              search={{ warehouseAreaId }}
            />
          ))}
        </Tabs>
        <Box className='flex min-h-0 grow flex-col px-1 pb-1 pt-6'>
          <ProductInventoryProvider inventory={inventory}>
            <Outlet />
          </ProductInventoryProvider>
        </Box>
      </Box>
      {isAdjustmentDialogOpen && (
        <AdjustmentDialog productInventory={inventory} onClose={() => setIsAdjustmentDialogOpen(false)} />
      )}
    </Paper>
  )
}
